import { ButtonBase } from "@mui/material";
import { MagnifyingGlassIcon, SortAscendingIcon } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { times } from "lodash-es";
import { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import { Autocomplete } from "@/components/Autocomplete/Autocomplete";
import { DrawerTopBar } from "@/components/Drawer/DrawerTopBar";
import { SwipeableDrawer } from "@/components/Drawer/SwipeableDrawer";
import { ErrorPanel } from "@/components/Errors/ErrorPanel";
import { IconAirplaneCrossed } from "@/components/Icons";
import { Skeleton } from "@/components/Skeleton/Skeleton";
import {
  TripCard,
  TripCardSkeleton,
} from "@/features/trips/components/TripCard";
import { TripsSort } from "@/features/trips/components/TripsSort";
import { tripsQueryOptions } from "@/features/trips/hooks/useTrips";
import type { TripsSortValue } from "@/features/trips/types/types";
import { initialSortValue } from "@/features/trips/utils/initialTripsValues";
import { rudderAnalytics, TripTrackingEvents } from "@/lib/rudderstack";

export function TripList() {
  const intl = useIntl();
  const navigate = useNavigate();
  const [countryFilter, setCountryFilter] = useState("");
  const [isSortDrawerOpen, setIsSortDrawerOpen] = useState(false);
  const [sortValue, setSortValue] = useState<TripsSortValue>(initialSortValue);

  const { data, isLoading, isError } = useQuery(
    tripsQueryOptions({
      sortField: sortValue.field,
      sortOrder: sortValue.order,
    }),
  );

  const countries = useMemo(() => {
    const allCountries = data?.flatMap((trip) => trip.countriesNames) || [];
    return Array.from(new Set(allCountries)).sort((a, b) => a.localeCompare(b));
  }, [data]);

  const isEmpty = !isLoading && (!data || data.length === 0);

  useEffect(() => {
    if (data) {
      rudderAnalytics.track(TripTrackingEvents.USER_VIEWED_TRIP_LIST);
    }
  }, [data]);

  return (
    <div>
      <div>
        {isError ? (
          <ErrorPanel
            icon={IconAirplaneCrossed}
            primaryText={intl.formatMessage({ id: "trips.errorState" })}
            secondaryText={intl.formatMessage({ id: "trips.errorStateDesc" })}
          />
        ) : isEmpty ? (
          <div className="mt-12 flex grow flex-col items-center justify-between gap-8">
            <ErrorPanel
              icon={IconAirplaneCrossed}
              primaryText={intl.formatMessage({ id: "trips.emptyState" })}
              secondaryText={intl.formatMessage({ id: "trips.emptyStateDesc" })}
            />
          </div>
        ) : (
          <div className="w-full">
            <div className="pb-4">
              {isLoading ? (
                <Skeleton width="100%" height={57} variant="rounded" />
              ) : (
                <div className="flex w-full gap-2">
                  <Autocomplete
                    options={countries}
                    value={countryFilter || null}
                    placeholder={intl.formatMessage({
                      id: "trips.searchByCountry",
                    })}
                    startIcon={
                      <MagnifyingGlassIcon
                        size={20}
                        color="var(--color-text-disabled-tertiary)"
                      />
                    }
                    onChange={(value) => setCountryFilter(value ?? "")}
                    fullWidth
                  />

                  <ButtonBase
                    aria-label={intl.formatMessage({ id: "trips.sorting" })}
                    onClick={() => setIsSortDrawerOpen(true)}
                    type="button"
                    sx={{
                      backgroundColor: "var(--color-surface-base-light)",
                      padding: "calc(var(--spacing)*4 - 1px)",
                      border: "1px solid var(--color-stroke-neutral-soft)",
                      borderRadius: "var(--radius-2xl)",
                      color: "var(--color-text-tertiary)",
                      ":hover": {
                        borderColor: "var(--color-stroke-neutral-strong)",
                      },
                    }}
                  >
                    <SortAscendingIcon size={24} color="currentColor" />
                  </ButtonBase>
                </div>
              )}
            </div>

            <ul className="space-y-3">
              {isLoading ? (
                <>
                  {times(3, (index) => (
                    <li key={index}>
                      <TripCardSkeleton />
                    </li>
                  ))}
                </>
              ) : (
                data
                  ?.filter(
                    (trip) =>
                      !countryFilter ||
                      trip.countriesNames.includes(countryFilter),
                  )
                  .map((trip) => (
                    <li key={trip.id}>
                      <TripCard
                        trip={trip}
                        onClick={() => {
                          navigate({
                            to: "/trips/$tripId",
                            params: { tripId: trip.id },
                          });
                        }}
                      />
                    </li>
                  ))
              )}
            </ul>
          </div>
        )}
      </div>

      <SwipeableDrawer
        open={isSortDrawerOpen}
        onClose={() => setIsSortDrawerOpen(false)}
      >
        <DrawerTopBar
          onCloseClick={() => setIsSortDrawerOpen(false)}
          title={intl.formatMessage({ id: "trips.sorting" })}
        />
        <TripsSort
          value={sortValue}
          onChange={(value) => {
            setSortValue(value);
            setIsSortDrawerOpen(false);
          }}
        />
      </SwipeableDrawer>
    </div>
  );
}
